import { registerComponent, asyncRequest, loading } from "helpers";
import WYATTElement from "wyatt";

registerComponent(
  "product-form",
  class ProductForm extends WYATTElement {
    constructor() {
      super();

      this.form = this.querySelector("form");

      this.form.addEventListener("submit", this.handleSubmit.bind(this));
      this.cart = null;
    }

    handleSubmit(event) {
      event.preventDefault();

      loading.add(this);
      this.addToCart();
    }

    /**
     * Get cart data
     * @returns {object} Cart data
     */
    async getCart() {
      const response = await fetch("/cart?view=protection-plan");
      if (!response.ok) {
        return null;
      }
      const data = await response.json();
      data.items.map((item) => {
        const { plans } = item;
        const planGroups = Object.groupBy(plans, (product) => product.group);
        item.plan_groups = planGroups;
        return item;
      });
      return data;
    }

    /**
     * Change item's _protection-plan property to new plan name
     * @param {object} item - Cart item
     * @param {string} planName - new plan name
     * @returns {object} Cart object
     */
    async changeItemPlan(item, planName) {
      return fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: item.key,
          quantity: item.quantity,
          properties: {
            ...item.properties,
            "_protection-plan": planName,
          },
          sections: WYATT.Cart.getSectionNames(),
        }),
      }).then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to update cart line item ${item.key}`);
        }
        return response.json();
      });
    }

    /**
     * Queues change to adjust properties to new plan
     * @returns {object|null} Cart object
     */
    async updateCartItemPlans() {
      const cart = await this.getCart();
      if (cart) {
        const { items } = cart;
        const cartPlanIds = items
          .map(({ is_plan: isPlan, variant_id: varId }) => {
            if (!isPlan) return null;
            return varId;
          })
          .filter(Boolean);

        items.forEach((item) => {
          if (item.is_plan || !item.properties?.["_protection-plan"]) return;
          const planName = item.properties["_protection-plan"];
          const hasPlanMissing = !item.plans?.some((plan) => {
            const planItem = items.find(
              (cartItem) =>
                cartItem.is_plan && cartItem.variant_id === plan.variant_id,
            );
            return planItem?.name === planName;
          });
          if (hasPlanMissing) {
            item.properties["_protection-plan"] = null;
          }
        });

        if (!cartPlanIds.length) {
          return Promise.resolve(null);
        }
        const missingItemGroups = items
          .filter(
            (item) =>
              !item.is_plan &&
              !item.properties?.["_protection-plan"] &&
              item.plans?.some(({ variant_id: varId }) =>
                cartPlanIds.includes(varId),
              ),
          )
          .map((item) => {
            const associatedPlan = item.plans.find(({ variant_id: varId }) =>
              cartPlanIds.includes(varId),
            );
            return {
              item,
              missingPlan: associatedPlan,
            };
          });

        if (!missingItemGroups.length) {
          return Promise.resolve(null);
        }

        return missingItemGroups.reduce(
          (queue, group) =>
            queue.then(() => {
              const { item, missingPlan } = group;
              return this.changeItemPlan(item, missingPlan.name);
            }),
          Promise.resolve(),
        );
      }

      return Promise.resolve(null);
    }

    /**
     * Verifies if protection plans to add are not already present in cart &
     * removes them accordingly.
     *
     * @param {Object[]} protectionPlans - Protection plans FormData array
     * @param {string} protectionPlans[].id - Variant Id
     * @param {number} protectionPlans[].quantity - Quantity to add
     * @returns {object[]} protection plans not already in cart
     */
    async verifyProtectionPlans(protectionPlans) {
      try {
        const fetchCart = await this.getCart();
        const cartItems = fetchCart?.items || [];
        const cartVariants = cartItems.map(
          ({ variant_id: variantId }) => variantId,
        );
        return protectionPlans.reduce((acc, plan) => {
          if (!cartVariants.some((id) => id === parseInt(plan.id, 10))) {
            acc.push(plan);
          }
          return acc;
        }, []);
      } catch (e) {
        window.console.error("Error fetching cart", e);
        return [];
      }
    }

    getFormAddons(formData, quantity = 1) {
      return [...formData.entries()].reduce((acc, [key, value]) => {
        if (!key.startsWith("addon")) return acc;
        acc.push({ id: parseInt(value, 10), quantity });
        return acc;
      }, []);
    }

    /**
     * Get recycling fees selected on the form. Fees are linked to the
     * covered item by variant id, so no follow-up request is needed to
     * discover a cart line key before linking.
     * @param {FormData} formData - Add to cart form data
     * @param {number} productQuantity - Quantity of the covered item, which
     * caps how many of each fee can be added.
     * @returns {object[]} Recycling fee data.
     */
    getFormRecyclingFees(formData, productQuantity) {
      return ["mattress", "foundation"].reduce((acc, feeType) => {
        const variantId = formData.get(`_recycling-fee[${feeType}]`);
        if (!variantId) return acc;
        const quantity =
          parseInt(formData.get(`_recycling-fee-quantity[${feeType}]`), 10) ||
          productQuantity;
        acc.push({
          id: parseInt(variantId, 10),
          quantity: Math.min(Math.max(quantity, 1), productQuantity),
          title: formData.get(`_recycling-fee-title[${feeType}]`) || "",
        });
        return acc;
      }, []);
    }

    /**
     * Get Protection Plans for form data.
     * @param {FormData} formData - Add to cart form data
     * @returns {object[]} Protection Plan data.
     */
    getFormProtectionPlans(formData) {
      const result = [...formData.entries()].reduce((acc, [key, value]) => {
        if (!key.startsWith("_protection-plan")) return acc;
        const keys = key.split(/[[\]]/).filter(Boolean);
        keys.reduce((current, currKey, index) => {
          const isLast = index === keys.length - 1;

          if (isLast) {
            if (current[currKey] !== undefined) {
              if (!Array.isArray(current[currKey]))
                current[currKey] = [current[currKey]];
              current[currKey].push(value);
            } else {
              current[currKey] = value;
            }
            return current;
          }

          if (
            typeof current[currKey] !== "object" ||
            current[currKey] === null ||
            Array.isArray(current[currKey])
          ) {
            current[currKey] = {};
          }

          return current[currKey];
        }, acc);

        return acc;
      }, {});
      return result;
    }

    /**
     * Removes plans from same group, favoring the most recently added.
     * @returns {object|null} Cart object
     */
    async handleDuplicatePlanGroups() {
      const { items } = await this.getCart();
      const planGroups = items.reduce((groups, item) => {
        if (item.is_plan) return groups;
        return (
          item.plans?.reduce((innerGroups, plan) => {
            innerGroups[plan.group] = [
              ...(innerGroups[plan.group] || []),
              plan.variant_id,
            ];
            return innerGroups;
          }, groups) ?? groups
        );
      }, {});
      /*
        Filter plans from plan group group and removing the first one.
        First plan would be the most recently added plan in cart since items are LIFO.
      */
      const duplicatePlanItems = Object.values(planGroups).flatMap(
        (variantIds) =>
          items
            .filter(
              (item) => item.is_plan && variantIds.includes(item.variant_id),
            )
            .slice(1),
      );

      if (duplicatePlanItems.length) {
        return WYATT.Cart.update({
          updates: duplicatePlanItems.reduce((acc, { key }) => {
            acc[key] = 0;
            return acc;
          }, {}),
        });
      }
      return null;
    }

    async getBody() {
      const formData = new FormData(this.form);

      const propertiesRegex = /properties\[(.*?)\]/;
      const attributesRegex = /attributes\[(.*?)\]/;
      const quantity = formData.get("quantity");

      const productData = {
        id: formData.get("id"),
        quantity,
        properties: formData.entries().reduce((acc, [key, value]) => {
          const match = key.match(propertiesRegex);
          if (match) {
            acc[match[1]] = value;
          }
          return acc;
        }, {}),
      };
      const items = [];
      const formPlans = this.getFormProtectionPlans(formData);
      const protectionPlans = Object.values(formPlans)
        .map((plan) => {
          const { id, quantity: qty, for: covers } = plan;
          const name = plan[id] || "Protection";
          const planQty = parseInt(qty, 10);
          return {
            id,
            name,
            quantity: planQty,
            covers: covers ? parseInt(covers, 10) : null,
          };
        })
        .filter((plan) => plan.id);

      const addonItems = this.getFormAddons(formData);
      const addonIDs = addonItems.map(({ id }) => id);
      /*
        A plan covering an add-on only applies while that add-on is selected.
        Hidden add-on selectors stay in the form, so the selection is the
        source of truth.
      */
      const applicablePlans = protectionPlans.filter(
        ({ covers }) => !covers || addonIDs.includes(covers),
      );

      const newPlans = applicablePlans.length
        ? await this.verifyProtectionPlans(applicablePlans)
        : [];

      if (newPlans.length) {
        const [protection] = newPlans
          .filter(({ covers }) => !covers)
          .map(({ name }) => name);
        if (protection) {
          // eslint-disable-next-line dot-notation
          productData.properties["_protection-plan"] = protection;
        }
        items.push(productData, ...newPlans);
      } else {
        items.push(productData);
      }

      if (addonItems.length) {
        items.push(
          ...addonItems.map((addon) => {
            const addonPlan = newPlans.find(
              ({ covers }) => covers === addon.id,
            );
            if (!addonPlan) return addon;
            return {
              ...addon,
              properties: { "_protection-plan": addonPlan.name },
            };
          }),
        );
      }

      const recyclingFees = this.getFormRecyclingFees(
        formData,
        parseInt(productData.quantity, 10) || 1,
      );
      if (recyclingFees.length) {
        items.push(
          ...recyclingFees.map(({ id, quantity: feeQuantity, title }) => ({
            id,
            quantity: feeQuantity,
            properties: {
              "_recycling-fee-parent-key": String(productData.id),
              "_recycling-fee-parent-title": title,
            },
          })),
        );
      }

      const storeAttribute = WYATT.PreferredStores?.serializeStore?.();

      const body = {
        items,
        sections: WYATT.Cart.getSectionNames(),
        attributes: storeAttribute ? { store: storeAttribute } : {},
      };

      // Add cart attributes or line item properties to body
      Array.from(formData).forEach(([name, value]) => {
        const attributesMatch = attributesRegex.exec(name);
        if (attributesMatch && attributesMatch.length > 1) {
          body.attributes[attributesMatch[1]] = value;
        }
      });

      return body;
    }

    async addToCart() {
      await WYATT.Cart.ready;

      const body = await this.getBody();
      const options = {
        ...WYATT.config.fetch.post,
        ...{ body: JSON.stringify(body) },
      };

      const addedToCartResponse = await asyncRequest(
        `${WYATT.routes.cart_add_url}.js`,
        options,
        {
          messageOptions: {
            minDelay: 6000,
            appendPoint:
              "[data-mini-cart] [data-dialog-messages], #page-messages",
          },
        },
      );

      if (addedToCartResponse) {
        try {
          await this.handleDuplicatePlanGroups();
          const updateCartPlanResponse = await this.updateCartItemPlans();
          if (!updateCartPlanResponse) {
            const data = JSON.parse(addedToCartResponse);
            await this.handleSuccessfulAdd(data);
          } else {
            await this.handleSuccessfulAdd(updateCartPlanResponse);
          }
        } catch (e) {
          window.console.error("Error updating cart item plans", e);
          const data = JSON.parse(addedToCartResponse);
          await this.handleSuccessfulAdd(data);
        }
      } else {
        await this.handleUnsuccessfulAdd();
      }

      loading.remove(this);
    }

    async handleSuccessfulAdd(response) {
      const result =
        typeof response?.item_count === "number"
          ? await WYATT.Cart.applyChangeResponse(response)
          : await WYATT.Cart.applyAddResponse(response);

      if (result?.sections) {
        this.publish(
          WYATT.PubSub.events.updateDOM,
          WYATT.Cart.sectionUpdates(result.sections),
        );
      }

      this.publish(WYATT.PubSub.events.addToCart);
      WYATT.Cart.syncBackorder();
    }

    async handleUnsuccessfulAdd() {
      await WYATT.Cart.reseed();

      const sectionsData = await asyncRequest(
        `${window.location.pathname}?sections=${WYATT.Cart.getSectionNames()}`,
        {},
        {
          responseFormat: "json",
        },
      );

      if (sectionsData) {
        this.publish(
          WYATT.PubSub.events.updateDOM,
          WYATT.Cart.sectionUpdates(sectionsData),
        );
      }

      this.publish(WYATT.PubSub.events.addToCart);
    }
  },
);
